import ApiRoutes from '@modules/account/defs/api-routes';
import {
  OpeningTimeData,
  WeeklyOpeningTimeInput,
  SpecificOpeningTimeInput,
  SpecificOpeningTime,
  CalendarAuthResponse,
  UnavailablePeriod,
  UnavailablePeriodInput,
} from '@modules/account/defs/types';
import useApi, { FetchApiOptions } from '@common/hooks/useApi';
import useSWR from 'swr';
import { useCallback } from 'react';

interface UseOpeningTimeReturn {
  data: OpeningTimeData | null;
  isLoading: boolean;
  error: Error | null;
  mutate: () => void;
  saveWeeklyOpeningTime: (
    openingTimes: WeeklyOpeningTimeInput[]
  ) => Promise<{ success: boolean; data?: unknown; errors?: string[] }>;
  saveSpecificOpeningTime: (
    openingTime: SpecificOpeningTimeInput
  ) => Promise<{ success: boolean; data?: SpecificOpeningTime; errors?: string[] }>;
  deleteSpecificOpeningTime: (id: number) => Promise<{ success: boolean; message?: string }>;
  saveUnavailablePeriod: (
    period: UnavailablePeriodInput
  ) => Promise<{ success: boolean; data?: UnavailablePeriod; errors?: string[] }>;
  deleteUnavailablePeriod: (id: number) => Promise<{ success: boolean; message?: string }>;
  connectCalendar: (provider: 'google' | 'microsoft', returnTo?: string) => Promise<void>;
  disconnectCalendar: (provider: string) => Promise<{ success: boolean; message?: string }>;
}

const useOpeningTimes = (): UseOpeningTimeReturn => {
  const fetchApi = useApi();

  const { data, error, mutate } = useSWR<OpeningTimeData | null>(
    ApiRoutes.GetOpeningTimes,
    async (url: string): Promise<OpeningTimeData | null> => {
      const response = await fetchApi<OpeningTimeData>(url, {
        method: 'GET',
      });

      if (response.success && response.data) {
        return response.data;
      }

      return {
        weeklyOpeningTimes: [],
        specificOpeningTimes: [],
        connectedCalendars: [],
        unavailablePeriods: [],
      };
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const formatTimeForBackend = (time: string): string => {
    if (!time) {
      return '00:00';
    }
    const parts = time.split(':');
    if (parts.length >= 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return time;
  };

  const saveWeeklyOpeningTime = useCallback(
    async (openingTimes: WeeklyOpeningTimeInput[], options?: FetchApiOptions) => {
      const groupedOpeningTimes = openingTimes.map((openingTime) => ({
        day_of_week: openingTime.dayOfWeek,
        slots: openingTime.slots.map((slot) => ({
          start_time: formatTimeForBackend(slot.startTime),
          end_time: formatTimeForBackend(slot.endTime),
        })),
      }));

      await mutate(
        async () => {
          const response = await fetchApi(ApiRoutes.SaveWeeklyOpeningTime, {
            method: 'POST',
            data: { openingTimes: groupedOpeningTimes },
            ...options,
          });

          if (response.success && response.data) {
            return {
              ...data,
              weeklyOpeningTimes: response.data,
            } as OpeningTimeData;
          }

          return data;
        },
        {
          optimisticData: data
            ? {
                ...data,
                weeklyOpeningTimes: [],
              }
            : null,
          revalidate: false,
        }
      );

      return { success: true };
    },
    [fetchApi, mutate, data]
  );

  const saveSpecificOpeningTime = useCallback(
    async (openingTime: SpecificOpeningTimeInput, options?: FetchApiOptions) => {
      const formattedData = {
        startTime: formatTimeForBackend(openingTime.startTime),
        endTime: formatTimeForBackend(openingTime.endTime),
        recurrence: openingTime.recurrence,
        startDate: openingTime.startDate,
        endDate: openingTime.endDate,
        metadata: openingTime.metadata,
      };

      const response = await fetchApi<SpecificOpeningTime>(ApiRoutes.SaveSpecificOpeningTime, {
        method: 'POST',
        data: formattedData,
        ...options,
      });

      if (response.success && response.data) {
        await mutate();
        return {
          success: response.success,
          data: response.data,
          errors: response.errors,
        };
      }

      return {
        success: response.success,
        errors: response.errors,
      };
    },
    [fetchApi, mutate]
  );

  const deleteSpecificOpeningTime = useCallback(
    async (id: number, options?: FetchApiOptions) => {
      const url = ApiRoutes.DeleteSpecificOpeningTime.replace('{id}', id.toString());

      await mutate(
        async () => {
          const response = await fetchApi(url, {
            method: 'DELETE',
            ...options,
          });

          if (response.success && data) {
            return {
              ...data,
              specificOpeningTimes: data.specificOpeningTimes.filter((item) => item.id !== id),
            };
          }

          return data;
        },
        {
          optimisticData: data
            ? {
                ...data,
                specificOpeningTimes: data.specificOpeningTimes.filter((item) => item.id !== id),
              }
            : null,
          revalidate: false,
        }
      );

      return { success: true };
    },
    [fetchApi, mutate, data]
  );

  const connectCalendar = useCallback(
    async (provider: 'google' | 'microsoft', returnTo?: string) => {
      try {
        const baseUrl = ApiRoutes.GetAuthUrl.replace('{provider}', provider);
        // Le backend ré-injecte ce path en redirect final après le callback OAuth pour
        // ramener l'user sur la page d'où il a déclenché la connexion (ex: settings).
        const url = returnTo ? `${baseUrl}?return_to=${encodeURIComponent(returnTo)}` : baseUrl;

        const response = await fetchApi<CalendarAuthResponse>(url, {
          method: 'GET',
        });

        if (response.success && response.data?.redirectUrl) {
          window.location.href = response.data.redirectUrl;
        } else {
          throw new Error(response.errors?.[0] || 'Failed to get calendar OAuth URL');
        }
      } catch (error) {
        console.error('Error connecting calendar:', error);
        throw error;
      }
    },
    [fetchApi]
  );

  const disconnectCalendar = useCallback(
    async (provider: string, options?: FetchApiOptions) => {
      const url = ApiRoutes.DisconnectCalendar.replace('{provider}', provider);

      await mutate(
        async () => {
          const response = await fetchApi(url, {
            method: 'DELETE',
            ...options,
          });

          if (response.success && data) {
            return {
              ...data,
              connectedCalendars: data.connectedCalendars.filter(
                (calendar) => calendar.provider !== provider
              ),
            };
          }

          return data;
        },
        {
          optimisticData: data
            ? {
                ...data,
                connectedCalendars: data.connectedCalendars.filter(
                  (calendar) => calendar.provider !== provider
                ),
              }
            : null,
          revalidate: false,
        }
      );

      return { success: true };
    },
    [fetchApi, mutate, data]
  );

  const saveUnavailablePeriod = useCallback(
    async (period: UnavailablePeriodInput, options?: FetchApiOptions) => {
      const response = await fetchApi<UnavailablePeriod>(ApiRoutes.SaveUnavailablePeriod, {
        method: 'POST',
        data: period,
        ...options,
      });

      if (response.success && response.data) {
        await mutate();
        return {
          success: response.success,
          data: response.data,
          errors: response.errors,
        };
      }

      return {
        success: response.success,
        errors: response.errors,
      };
    },
    [fetchApi, mutate]
  );

  const deleteUnavailablePeriod = useCallback(
    async (id: number, options?: FetchApiOptions) => {
      const url = ApiRoutes.DeleteUnavailablePeriod.replace('{id}', id.toString());

      await mutate(
        async () => {
          const response = await fetchApi(url, {
            method: 'DELETE',
            ...options,
          });

          if (response.success && data) {
            return {
              ...data,
              unavailablePeriods: data.unavailablePeriods.filter((period) => period.id !== id),
            };
          }

          return data;
        },
        {
          optimisticData: data
            ? {
                ...data,
                unavailablePeriods: data.unavailablePeriods.filter((period) => period.id !== id),
              }
            : null,
          revalidate: false,
        }
      );

      return { success: true };
    },
    [fetchApi, mutate, data]
  );

  return {
    data: data ?? null,
    isLoading: !error && data === undefined,
    error,
    mutate,
    saveWeeklyOpeningTime,
    saveSpecificOpeningTime,
    deleteSpecificOpeningTime,
    saveUnavailablePeriod,
    deleteUnavailablePeriod,
    connectCalendar,
    disconnectCalendar,
  };
};

export default useOpeningTimes;
